import { AsyncPipe } from '@angular/common';
import { Component, effect, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeviceStoreSelectors } from '@era/stores';
import { Store } from '@ngrx/store';
import { DeviceState, DeviceType } from '@rocksoft/locations-sdk';
import { Device } from '@rocksoft/shared';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DevicesStateListComponent } from './devices-state-list.component';

export type DevicesStateListState = 'unassigned' | 'unavailable';

const stateConfig: Record<DevicesStateListState, { title: string; emptyMessage: string }> = {
  unassigned: {
    title: 'Unassigned',
    emptyMessage: 'No unassigned devices.',
  },
  unavailable: {
    title: 'Unavailable',
    emptyMessage: 'No unavailable devices.',
  },
};

function deviceStateFor(state: DevicesStateListState): DeviceState {
  return state === 'unassigned' ? DeviceState.Unassigned : DeviceState.Unavailable;
}

@Component({
  selector: 'era-devices-state-list',
  templateUrl: './devices-state-list.container.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DevicesStateListComponent, AsyncPipe],
})
export class DevicesStateListContainerComponent {
  private readonly deviceStore = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private readonly stateSubject = new BehaviorSubject<DevicesStateListState>('unassigned');

  readonly stateInput = input.required<DevicesStateListState>();

  public readonly state$ = this.stateSubject.asObservable();
  public readonly devices$: Observable<Device[]>;
  public readonly config$ = this.stateSubject.pipe(map((s) => stateConfig[s]));

  constructor() {
    effect(() => {
      const value = this.stateInput();
      if (value) this.stateSubject.next(value);
    });

    this.devices$ = this.stateSubject.pipe(
      switchMap((s) => this.deviceStore.select(DeviceStoreSelectors.selectDevicesByState(deviceStateFor(s))))
    );
  }

  public itemSelected(device: Device): void {
    if (device.deviceType === DeviceType.Apple) {
      void this.router.navigate(['apple', device.udid], { relativeTo: this.route });
    }
  }
}
